import { useParams } from 'react-router';
import { useCorrectiveActionDetail, useCompleteCaActivity } from '@/api/competency';
import { BackButton } from '@/components/shared/back-button';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { Clock, ShieldAlert } from 'lucide-react';
import type { CorrectiveActionStatus } from '@/types';
import { ActivityRow } from './_components/activity-row';

const TRIGGER_BADGES: Record<string, string> = {
  assessment_failure: 'bg-red-100 text-red-700',
  assessment_failed: 'bg-red-100 text-red-700',
  quiz_failure: 'bg-amber-100 text-amber-700',
  quiz_failed: 'bg-amber-100 text-amber-700',
  manual: 'bg-gray-100 text-gray-600',
  competency_gap: 'bg-purple-100 text-purple-700',
  overdue_training: 'bg-orange-100 text-orange-700',
};

const TRIGGER_LABELS: Record<string, string> = {
  assessment_failure: 'Evaluación reprobada',
  assessment_failed: 'Evaluación reprobada',
  quiz_failure: 'Quiz reprobado',
  quiz_failed: 'Quiz reprobado',
  manual: 'Manual',
  competency_gap: 'Brecha de competencia',
  overdue_training: 'Capacitación vencida',
};

const STATUS_BADGES: Record<CorrectiveActionStatus, string> = {
  open: 'bg-red-100 text-red-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<CorrectiveActionStatus, string> = {
  open: 'Abierta',
  in_progress: 'En Progreso',
  resolved: 'Completada',
};

function isOverdue(dueDate: string | null, status: CorrectiveActionStatus): boolean {
  if (!dueDate || status === 'resolved') return false;
  return new Date(dueDate).getTime() < Date.now();
}

export function CorrectiveActionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const actionId = Number(id) || 0;
  const { data: action, isLoading, error } = useCorrectiveActionDetail(actionId);
  const completeActivity = useCompleteCaActivity();

  if (isLoading) return <LoadingSpinner />;
  if (error || !action) {
    return <ErrorMessage message="No se pudo cargar el detalle de la acción correctiva." />;
  }

  const overdue = isOverdue(action.due_date, action.status);
  // #4: activities unlock in `sequence` order; an activity is locked while any
  // PRECEDING required activity is still incomplete (intuitive 1-2-3 flow).
  const activities = [...(action.activities ?? [])].sort(
    (a, b) => a.sequence - b.sequence || a.id - b.id,
  );

  return (
    <div className="space-y-6">
      <BackButton fallback="/competency" />

      {/* Encabezado */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              TRIGGER_BADGES[action.trigger_type],
            )}
          >
            {TRIGGER_LABELS[action.trigger_type] ?? action.trigger_type}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_BADGES[action.status],
            )}
          >
            {STATUS_LABELS[action.status]}
          </span>
          {action.is_escalated && (
            <span className="flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
              <ShieldAlert className="h-3 w-3" /> Escalada
            </span>
          )}
          {overdue && (
            <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              <Clock className="h-3 w-3" /> Vencida
            </span>
          )}
        </div>

        {/* Avance derivado — "Completada" solo al 100% */}
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium text-muted-foreground">Avance</span>
            <span className="font-semibold text-foreground">{action.progress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                action.status === 'resolved' ? 'bg-emerald-500' : 'bg-primary',
              )}
              style={{ width: `${action.progress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            La acción se marca como «Completada» automáticamente cuando termines todas las
            actividades requeridas.
          </p>
        </div>
      </div>

      {/* Descripción */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-medium text-foreground">Descripción</h2>
        <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
          {action.description}
        </p>
      </div>

      {/* Actividades requeridas con enlace a cada una */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-medium text-foreground">Actividades requeridas</h2>
        {activities.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Aún no hay actividades definidas. Tu instructor las asignará pronto.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {activities.map((a, idx) => (
              <ActivityRow
                key={a.id}
                activity={a}
                actionId={actionId}
                locked={activities
                  .slice(0, idx)
                  .some((p) => p.is_required && p.completed_at == null)}
                onConfirm={(activityId) => completeActivity.mutate({ id: actionId, activityId })}
                confirming={completeActivity.isPending}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Fechas */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-medium text-foreground">Fechas</h2>
        <div className="mt-3 space-y-1 text-sm">
          <p>
            <span className="font-medium text-foreground">Creada:</span>{' '}
            <span className="text-muted-foreground">{formatDate(action.created_at)}</span>
          </p>
          {action.resolved_at && (
            <p>
              <span className="font-medium text-foreground">Completada:</span>{' '}
              <span className="text-muted-foreground">{formatDate(action.resolved_at)}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
